/* global VXConfig */
import PropTypes from 'prop-types';
import Flux from '../../flux/Flux';
import Translations from '../../utils/Translations';
import { getTimer } from '../Dailybonus/TimerHelper';
import VipBadge from '../Dailybonus/SubComponents/VipBadge';
import { generateKey } from '../../utils/CommonUtils';
import './../../../../src/VX/Asset/Less/VX/07-components/actor/heatmap.less';
import { useEffect, useRef, useState } from 'react';

const countOfLineY = 6;
const heightMobileChart = 118;

function hexToRgbA(hex, alpha = 1) {
	let c;
	if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
		c = hex.substring(1).split('');
		if (c.length === 3) {
			c = [c[0], c[0], c[1], c[1], c[2], c[2]];
		}
		c = '0x' + c.join('');
		return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${alpha})`;
	}
	throw new Error('Bad Hex');
}

const ModelHeatMapChart = (props) => {
	const { daysActivity, defaultBarsColor, restricted, containerRef, lineColor, isOverview, isMobile } = props;

	const chartContainerRef = useRef(null);

	const [dimensions, setDimensions] = useState({
		width: 0,
		height: isOverview ? 250 : 80,
	});

	useEffect(() => {
		const setChartSize = () => {
			const refWidth = isOverview ? chartContainerRef.current?.clientWidth : containerRef.current?.clientWidth;
			setDimensions({
				width: refWidth,
				height: VXConfig.isVXMobilePlatform || isMobile ? heightMobileChart : refWidth / 5,
			});
		};

		setChartSize();

		Flux.Browser.addWindowResizeListener(setChartSize);

		return () => {
			Flux.Browser.removeWindowResizeListener(setChartSize);
		};
	}, [containerRef?.current?.clientWidth, chartContainerRef?.current?.clientWidth]);

	const { width, height } = dimensions;
	if (width <= 0) return <div />;

	const svgStyle = { width: `${width}px`, height: `${height + 1}px` };
	const columnLength = daysActivity.length;
	const paddingTop = 10;
	const paddingLeft = 30;
	const yStep = (height - paddingTop) / countOfLineY;
	const widthChart = width - paddingLeft;
	const widthBar = widthChart / columnLength / 2;
	const heightOfOneHour = (height - paddingTop) / 24;
	const yTimeInterval = 4;
	const BERLIN = getTimer().fullTime;
	const currentDay = BERLIN.getDay() === 0 ? 6 : BERLIN.getDay() - 1;

	return (
		<div className={`heatmap-chart ${isOverview ? '-heatmap-overview' : ''}`}>
			{restricted && (
				<div className={'heatmap-chart__not-vip-blur' + (Flux.Browser.isIE() ? ' -ie-opacity' : '')}>
					<i className="icon -icon-keylock_close" />
					<div className="actor-heatmap-block__vip-badge">
						<VipBadge show={true} />
					</div>
				</div>
			)}
			<div className={restricted ? 'heatmap-chart__header--blur' : ''} ref={chartContainerRef}>
				<div className="heatmap-chart__header">
					{daysActivity.map((day, index) => {
						const locales = VXConfig.language === 'de' ? 'de-DE' : 'en-US';
						const localDay = new Intl.DateTimeFormat(locales, { weekday: 'long' }).format(
							new Date(day.date.substr(0, 10).replace(/-/g, '/')),
						);
						return (
							<div key={index} className={`heatmap-chart__header--x-point-name ${index === currentDay ? 'bold' : 'light'}`}>
								{VXConfig.isVXMobilePlatform || isMobile ? localDay.substring(0, 2) + '.' : localDay}
							</div>
						);
					})}
				</div>
				<svg className="chart" style={svgStyle} preserveAspectRatio="none" role="img">
					{daysActivity.map(
						(day, index) =>
							index < daysActivity.length && (
								<text key={index} className="chart__grid-y-time" x="20" y={yStep * index + paddingTop}>
									{yTimeInterval * index}
								</text>
							),
					)}
					{daysActivity.map((day, index) => (
						<line
							key={index}
							x1="30"
							y1={yStep * index + paddingTop}
							x2={width}
							y2={yStep * index + paddingTop}
							style={{ stroke: lineColor, strokeWidth: 1 }}
						/>
					))}
					{daysActivity.map((day, index) =>
						day.activity.map((activity, idx) => (
							<rect
								key={generateKey(idx)}
								fill={hexToRgbA(defaultBarsColor, activity.level)}
								width={widthBar}
								height={Number((heightOfOneHour * (activity.end - activity.start)).toFixed(2))}
								x={(widthChart / columnLength) * index + paddingLeft + widthBar / 2}
								y={heightOfOneHour * activity.start + paddingTop}
								className="bars"
							/>
						)),
					)}
				</svg>
				<div className="heatmap-chart__info">
					<div className="heatmap-chart__info--item">
						<span className="heatmap-chart__info--item__bar hard" />
						<span className="heatmap-chart__info--item__text">{Translations.get('ActorProfileHeatMapBarHard')}</span>
					</div>
					<div className="heatmap-chart__info--item medium">
						<span className="heatmap-chart__info--item__bar medium" />
						<span className="heatmap-chart__info--item__text">{Translations.get('ActorProfileHeatMapBarMedium')}</span>
					</div>
					<div className="heatmap-chart__info--item">
						<span className="heatmap-chart__info--item__bar low" />
						<span className="heatmap-chart__info--item__text">{Translations.get('ActorProfileHeatMapBarLow')}</span>
					</div>
				</div>
			</div>
		</div>
	);
};

ModelHeatMapChart.defaultProps = {
	daysActivity: [
		{
			date: '2019-08-26',
			activity: [
				{
					level: 1,
					start: 0,
					end: 1,
				},
				{
					level: 0.6,
					start: 1,
					end: 4,
				},
				{
					level: 0.2,
					start: 4,
					end: 5,
				},
				{
					level: 0.2,
					start: 18,
					end: 22,
				},
				{
					level: 0.6,
					start: 22,
					end: 24,
				},
			],
		},
		{
			date: '2019-08-27',
			activity: [
				{
					level: 1,
					start: 0,
					end: 1,
				},
				{
					level: 0.6,
					start: 1,
					end: 3,
				},
				{
					level: 0.2,
					start: 3,
					end: 4,
				},
				{
					level: 0.2,
					start: 18,
					end: 19,
				},
				{
					level: 0.6,
					start: 19,
					end: 22,
				},
				{
					level: 1,
					start: 22,
					end: 24,
				},
			],
		},
		{
			date: '2019-08-28',
			activity: [
				{
					level: 0.6,
					start: 0,
					end: 4,
				},
				{
					level: 0.2,
					start: 16,
					end: 18,
				},
				{
					level: 0.6,
					start: 18,
					end: 22,
				},
				{
					level: 1,
					start: 22,
					end: 24,
				},
			],
		},
		{
			date: '2019-08-29',
			activity: [
				{
					level: 0.2,
					start: 11,
					end: 20,
				},
				{
					level: 0.6,
					start: 20,
					end: 24,
				},
			],
		},
		{
			date: '2019-08-30',
			activity: [
				{
					level: 0.2,
					start: 5,
					end: 8,
				},
				{
					level: 0.6,
					start: 8,
					end: 12,
				},
				{
					level: 1,
					start: 12,
					end: 14,
				},
				{
					level: 0.6,
					start: 14,
					end: 18,
				},
				{
					level: 0.2,
					start: 18,
					end: 22,
				},
			],
		},
		{
			date: '2019-08-31',
			activity: [
				{
					level: 1,
					start: 0,
					end: 1,
				},
				{
					level: 0.6,
					start: 1,
					end: 3,
				},
			],
		},
		{
			date: '2019-09-1',
			activity: [
				{
					level: 0.2,
					start: 0,
					end: 1,
				},
				{
					level: 0.2,
					start: 18,
					end: 20,
				},
				{
					level: 0.6,
					start: 20,
					end: 23,
				},
				{
					level: 1,
					start: 23,
					end: 24,
				},
			],
		},
	],
	defaultBarsColor: '#43b31c',
	restricted: true,
	nowIndicatorColor: '#ffffff',
	lineColor: '#2f3032',
	isMobile: false,
	containerRef: null,
};

ModelHeatMapChart.propTypes = {
	daysActivity: PropTypes.arrayOf(
		PropTypes.shape({
			date: PropTypes.string,
			activity: PropTypes.arrayOf(
				PropTypes.shape({
					level: PropTypes.number,
					start: PropTypes.number,
					end: PropTypes.number,
				}),
			),
		}),
	),
	defaultBarsColor: PropTypes.string,
	isOverview: PropTypes.bool,
	nowIndicatorColor: PropTypes.string,
	lineColor: PropTypes.string,
	restricted: PropTypes.bool,
	isMobile: PropTypes.bool,
	containerRef: PropTypes.object,
};

export default ModelHeatMapChart;
